import type { TeamMemberTile } from '@dcc/shared';
import { CapacityLayer, FreshnessTag, StateChip, Unknown } from './Primitives';
import { toneFor } from '../lib/tone';
import { relativeTime } from '../lib/format';

/**
 * A real colleague and their licensed seat — never an agent card.
 *
 * Low provider consumption is phrased as unused Claude capacity. The tile makes
 * no claim about the person's effort, and it never suggests using someone
 * else's seat.
 */
export function CrewTile({
  member,
  onHandoff,
}: {
  member: TeamMemberTile;
  onHandoff?: (member: TeamMemberTile) => void;
}) {
  const tone = toneFor('member', member.status);
  return (
    <article className="panel stack" data-tone={tone} aria-labelledby={`crew-${member.id}`}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <div>
          <h3 className="tile__name" id={`crew-${member.id}`}>
            {member.name}
          </h3>
          <div className="tile__version">{member.role}</div>
        </div>
        <StateChip kind="member" state={member.status} />
      </div>

      <p className="small dim">{member.statusDetail}</p>

      <div className="small">
        <div className="tile__row">
          <span className="dim">Seat</span>
          <span>
            {member.seat ? (
              <>
                {member.seat.provider} {member.seat.plan}{' '}
                <span className="dim">· {member.seat.state.toLowerCase()}</span>
              </>
            ) : (
              <Unknown why="No licensed seat is assigned to this colleague." />
            )}
          </span>
        </div>
        <div className="tile__row">
          <span className="dim">Working on</span>
          <span className="truncate">
            {member.currentProjectName ?? '—'}
            {member.currentWorkstream ? ` · ${member.currentWorkstream}` : ''}
          </span>
        </div>
        <div className="tile__row">
          <span className="dim">Session</span>
          <span className="mono">
            {member.currentSession ? member.currentSession.providerSessionId : '—'}
          </span>
        </div>
      </div>

      <div className="stack stack--tight">
        <CapacityLayer layer={member.sessionRange} title="Session range" compact />
        <div className="divider" />
        <CapacityLayer layer={member.weeklyFuel} title="Weekly fuel" compact />
        <div className="divider" />
        <CapacityLayer layer={member.seatPlan} title="Seat / plan capacity" compact />
      </div>

      <dl
        className="small"
        style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr auto', gap: 4 }}
      >
        <dt className="dim">Productive</dt>
        <dd className="mono" style={{ margin: 0 }}>
          {member.productiveMinutes}m
        </dd>
        <dt className="dim">Waiting</dt>
        <dd className="mono" style={{ margin: 0 }}>
          {member.waitingMinutes}m
        </dd>
        <dt className="dim">Blocked by a limit</dt>
        <dd className="mono" style={{ margin: 0 }}>
          {member.limitBlockedMinutes}m
        </dd>
        <dt className="dim">Unused Claude capacity</dt>
        <dd className="mono" style={{ margin: 0 }}>
          {member.unusedCapacityPercent === null ? <Unknown /> : `${member.unusedCapacityPercent}%`}
        </dd>
      </dl>

      <div className="spread">
        <FreshnessTag freshness={member.freshness} />
        {member.handoffEligible ? (
          onHandoff ? (
            <button type="button" className="btn btn--sm" onClick={() => onHandoff(member)}>
              Hand work over
            </button>
          ) : (
            <span className="chip" data-tone="success">
              can take over
            </span>
          )
        ) : (
          <span
            className="chip"
            data-tone="neutral"
            title={member.handoffBlockedReason ?? undefined}
          >
            not eligible now
          </span>
        )}
      </div>
      {!member.handoffEligible && member.handoffBlockedReason && (
        <p className="tiny dim">{member.handoffBlockedReason}</p>
      )}
      <p className="tiny dim">Last activity {relativeTime(member.lastActivityAt)}</p>
    </article>
  );
}
